<template>
  <a-modal title="Create Record" :visible="visible" width="500px" @cancel="cancel" :destroy-on-close="true">
    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-6"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex flex-column gap-2">
        <a-form-model-item :colon="false" label-align="left" label="Category">
          <custom-select
            :multiple="false"
            :value.sync="type"
            placeholder="Category"
            :options="types"
            name="type_name"
            optionKey="type"
          />
        </a-form-model-item>
        <a-form-model-item v-if="type" :colon="false" label-align="left" label="Event" prop="action_type_id">
          <a-select
            show-search
            :autoClearSearchValue="false"
            :allow-clear="true"
            :filter-option="false"
            placeholder="Event"
            v-model="form.action_type_id"
          >
            <a-select-opt-group label="Positive">
              <a-select-option
                v-for="option in filteredSubtypes.filter(item => !item.is_negative)"
                :key="option.id"
                :value="option.id"
              >
                {{ option.sub_type_name }} ({{ option.value }} points)
              </a-select-option>
            </a-select-opt-group>
            <a-select-opt-group label="Negative">
              <a-select-option
                v-for="option in filteredSubtypes.filter(item => item.is_negative)"
                :key="option.id"
                :value="option.id"
              >
                {{ option.sub_type_name }} ({{ option.value }} points)
              </a-select-option>
            </a-select-opt-group>
          </a-select>
        </a-form-model-item>
        <a-form-model-item
          v-if="currentType?.custom_value"
          :colon="false"
          label-align="left"
          label="Reason"
          prop="reason"
        >
          <a-input v-model="form.reason" placeholder="Enter Reason" />
        </a-form-model-item>
        <a-form-model-item v-if="currentType" :colon="false" label-align="left" label="Points" prop="value" :min="0">
          <a-input-number v-model="form.value" :disabled="!currentType.custom_value" placeholder="Enter Points" />
        </a-form-model-item>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="cancel"> Cancel </a-button>
        <a-button icon="save" type="primary" @click="submit" :loading="loading"> Create </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import CustomSelect from '@/components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import { Button, FormModel, Input, InputNumber, Modal, notification, Select } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const {
  mapState: pointSystemState,
  mapActions: pointSystemActions,
  mapGetters: pointSystemGetters
} = createNamespacedHelpers('pointSystem')
const { mapState: profileState } = createNamespacedHelpers('profile')

const initialForm = {
  action_type_id: undefined,
  value: undefined,
  reason: undefined
}

const initialRules = {
  action_type_id: [
    {
      required: true,
      message: 'Event can not be empty',
      trigger: 'change'
    }
  ],
  value: [
    {
      required: true,
      message: 'Points can not be empty',
      trigger: 'change'
    }
  ],
  reason: [
    {
      required: true,
      message: 'Reason can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-modal': Modal,
    'a-input': Input,
    'a-input-number': InputNumber,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-select': Select,
    'a-select-opt-group': Select.OptGroup,
    'a-select-option': Select.Option,
    'a-button': Button,
    'custom-select': CustomSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,

      type: undefined,

      form: { ...initialForm },
      rules: { ...initialRules }
    }
  },
  watch: {
    currentType: {
      handler(newVal) {
        if (newVal) {
          this.form.value = newVal.value
          this.form.reason = undefined
        }
      }
    },
    type: {
      handler() {
        this.form.action_type_id = undefined
        this.form.value = undefined
        this.form.reason = undefined
      }
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...pointSystemState({
      statuses: state => state.statuses,
      subtypes: state => state.types,
      groups: state => state.groups
    }),
    ...pointSystemGetters(['getTypes']),

    currentType() {
      return this.getTypes(this.form.action_type_id)
    },
    filteredSubtypes() {
      return this.subtypes.filter(item => item.type === this.type)
    },
    isShowPoints() {
      return this.associate?.point_system?.point_system_status?.is_show_points ?? false
    },
    types() {
      return Array.from(
        new Map((this.subtypes ?? []).map(item => [item.type, { type: item.type, type_name: item.type_name }])).values()
      ).filter(item => {
        if (!this.isShowPoints) {
          return item.type === 'other'
        }

        return true
      })
    }
  },
  methods: {
    ...pointSystemActions(['postAction']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    cancel() {
      this.form = deepCopy(initialForm)
      this.type = undefined
      this.$emit('update:visible', false)
    },

    async submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.loading = true

        try {
          const payload = {
            ...this.form,
            associate_id: this.associate.id
          }

          await this.postAction(payload)

          this.$emit('add')
          this.cancel()
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>
<style scoped>
::v-deep .ant-select-selection {
  max-height: 200px;
  height: fit-content;
  overflow: auto;
}
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
</style>
